<template>
  <transition name="ob-fade">
    <div v-if="visible" class="ob-overlay">
      <div class="ob-card">

        <!-- Top bar: progress (sem skip — obrigatório) -->
        <div class="ob-topbar">
          <div class="ob-progress">
            <div class="ob-progress-bar" :style="{ width: progressPct + '%' }" />
          </div>
          <span v-if="step > 0 && step < 5" class="ob-step-counter">{{ step }}/4</span>
        </div>

        <!-- Step 0: Boas-vindas -->
        <div v-if="step === 0" class="ob-step">
          <div class="ob-emoji">&#128075;</div>
          <h2 class="ob-title">Bem-vindo ao VSLBooster, {{ firstName }}!</h2>
          <p class="ob-desc">Vamos personalizar sua experiencia para que voce aproveite ao maximo a plataforma. Sao apenas 4 perguntas rapidas.</p>
          <button class="ob-btn ob-btn--primary" @click="next">
            Vamos comecar
            <feather-icon icon="ArrowRightIcon" size="16" />
          </button>
        </div>

        <!-- Step 1: Site + Nicho -->
        <div v-if="step === 1" class="ob-step">
          <div class="ob-step-header">
            <h3 class="ob-step-title">Sobre seu negocio</h3>
          </div>

          <div class="ob-field">
            <label class="ob-label">Qual o site/dominio do seu negocio?</label>
            <input
              v-model="form.siteUrl"
              class="ob-input"
              placeholder="www.meusite.com.br"
              @keydown.enter="next"
            />
            <span class="ob-hint">Opcional - voce pode adicionar depois</span>
          </div>

          <div class="ob-field">
            <label class="ob-label">Qual seu nicho de atuacao? <span class="ob-required-star">*</span></label>
            <div class="ob-chips">
              <button
                v-for="n in niches"
                :key="n"
                class="ob-chip"
                :class="{ 'ob-chip--active': form.niche === n }"
                @click="form.niche = n"
              >{{ n }}</button>
            </div>
          </div>

          <div class="ob-nav">
            <button class="ob-btn ob-btn--ghost" @click="prev">Voltar</button>
            <button class="ob-btn ob-btn--primary" :disabled="!form.niche" @click="next">
              Proximo
              <feather-icon icon="ArrowRightIcon" size="14" />
            </button>
          </div>
          <span v-if="!form.niche" class="ob-required-hint">Selecione seu nicho para continuar</span>
        </div>

        <!-- Step 2: Uso de VSL -->
        <div v-if="step === 2" class="ob-step">
          <div class="ob-step-header">
            <h3 class="ob-step-title">Uso de VSL</h3>
          </div>

          <div class="ob-field">
            <label class="ob-label">Voce ja usa VSL no seu site? <span class="ob-required-star">*</span></label>
            <div class="ob-yesno">
              <button
                class="ob-yn-btn"
                :class="{ 'ob-yn-btn--active': form.usesVsl === true }"
                @click="form.usesVsl = true"
              >
                <feather-icon icon="CheckIcon" size="15" />
                Sim
              </button>
              <button
                class="ob-yn-btn"
                :class="{ 'ob-yn-btn--active': form.usesVsl === false }"
                @click="form.usesVsl = false; form.vslPlatform = ''"
              >
                <feather-icon icon="XIcon" size="15" />
                Nao
              </button>
            </div>
          </div>

          <transition name="ob-slide">
            <div v-if="form.usesVsl === true" class="ob-field">
              <label class="ob-label">Qual plataforma voce usa? <span class="ob-required-star">*</span></label>
              <div class="ob-chips">
                <button
                  v-for="p in vslPlatforms"
                  :key="p"
                  class="ob-chip"
                  :class="{ 'ob-chip--active': form.vslPlatform === p }"
                  @click="form.vslPlatform = p"
                >{{ p }}</button>
              </div>
            </div>
          </transition>

          <div class="ob-nav">
            <button class="ob-btn ob-btn--ghost" @click="prev">Voltar</button>
            <button class="ob-btn ob-btn--primary" :disabled="!canProceedStep2" @click="next">
              Proximo
              <feather-icon icon="ArrowRightIcon" size="14" />
            </button>
          </div>
          <span v-if="!canProceedStep2" class="ob-required-hint">
            {{ form.usesVsl === null ? 'Responda se ja usa VSL para continuar' : 'Selecione a plataforma para continuar' }}
          </span>
        </div>

        <!-- Step 3: Objetivo -->
        <div v-if="step === 3" class="ob-step">
          <div class="ob-step-header">
            <h3 class="ob-step-title">Seu objetivo principal</h3>
          </div>

          <div class="ob-cards-grid">
            <button
              v-for="g in goals"
              :key="g.value"
              class="ob-option-card"
              :class="{ 'ob-option-card--active': form.goal === g.value }"
              @click="form.goal = g.value"
            >
              <feather-icon :icon="g.icon" size="22" />
              <span>{{ g.label }}</span>
            </button>
          </div>

          <div class="ob-nav">
            <button class="ob-btn ob-btn--ghost" @click="prev">Voltar</button>
            <button class="ob-btn ob-btn--primary" :disabled="!form.goal" @click="next">
              Proximo
              <feather-icon icon="ArrowRightIcon" size="14" />
            </button>
          </div>
          <span v-if="!form.goal" class="ob-required-hint">Selecione seu objetivo para continuar</span>
        </div>

        <!-- Step 4: Como conheceu -->
        <div v-if="step === 4" class="ob-step">
          <div class="ob-step-header">
            <h3 class="ob-step-title">Como voce conheceu o VSLBooster?</h3>
          </div>

          <div class="ob-cards-grid ob-cards-grid--sm">
            <button
              v-for="r in referrals"
              :key="r"
              class="ob-option-card ob-option-card--sm"
              :class="{ 'ob-option-card--active': form.referral === r }"
              @click="form.referral = r"
            >{{ r }}</button>
          </div>

          <div class="ob-nav">
            <button class="ob-btn ob-btn--ghost" @click="prev">Voltar</button>
            <button class="ob-btn ob-btn--primary" :disabled="!form.referral || saving" @click="finish">
              <span v-if="saving" class="ob-btn-spinner" />
              {{ saving ? 'Salvando...' : 'Finalizar' }}
            </button>
          </div>
          <span v-if="!form.referral" class="ob-required-hint">Selecione como nos conheceu para finalizar</span>
        </div>

        <!-- Step 5: Concluido -->
        <div v-if="step === 5" class="ob-step ob-step--done">
          <div class="ob-emoji">&#127881;</div>
          <h2 class="ob-title">Tudo pronto!</h2>
          <p class="ob-desc">Sua conta esta configurada. Agora e so comecar a usar o VSLBooster para turbinar seus resultados.</p>
          <div class="ob-tips">
            <div class="ob-tip">
              <feather-icon icon="VideoIcon" size="16" />
              <span>Faca upload do seu primeiro video</span>
            </div>
            <div class="ob-tip">
              <feather-icon icon="MessageSquareIcon" size="16" />
              <span>Crie seu primeiro fluxo de chat</span>
            </div>
            <div class="ob-tip">
              <feather-icon icon="BarChart2Icon" size="16" />
              <span>Acompanhe as metricas no dashboard</span>
            </div>
          </div>
          <button class="ob-btn ob-btn--primary ob-btn--lg" @click="close">
            Comecar a usar
            <feather-icon icon="ZapIcon" size="16" />
          </button>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
import { useApi } from '@/composables/useApi'

const api = useApi()

export default {
  name: 'OnboardingWizard',

  data() {
    return {
      visible: true,
      step: 0,
      saving: false,
      form: {
        siteUrl: '',
        niche: '',
        usesVsl: null,
        vslPlatform: '',
        goal: '',
        referral: '',
      },
      niches: ['E-commerce', 'Infoprodutos', 'SaaS', 'Agencia', 'Consultoria', 'Educacao', 'Saude', 'Outro'],
      vslPlatforms: ['Vturb', 'Panda Video', 'YouTube', 'Hotmart', 'Wistia', 'Vimeo', 'Outra'],
      goals: [
        { value: 'conversao',   label: 'Aumentar conversao',    icon: 'TrendingUpIcon' },
        { value: 'engajamento', label: 'Melhorar engajamento',  icon: 'HeartIcon' },
        { value: 'leads',       label: 'Capturar mais leads',   icon: 'UsersIcon' },
        { value: 'analytics',   label: 'Entender metricas',     icon: 'BarChart2Icon' },
        { value: 'funil',       label: 'Otimizar funil',        icon: 'FilterIcon' },
        { value: 'outro',       label: 'Outro objetivo',        icon: 'TargetIcon' },
      ],
      referrals: ['Google', 'YouTube', 'Instagram', 'Facebook', 'Indicacao', 'Podcast', 'Blog/Artigo', 'Outro'],
    }
  },

  computed: {
    firstName() {
      const ud = this.getUserData()
      return (ud.user_name || '').split(' ')[0] || 'usuario'
    },
    progressPct() {
      return Math.round((this.step / 5) * 100)
    },
    canProceedStep2() {
      if (this.form.usesVsl === null) return false
      if (this.form.usesVsl === true && !this.form.vslPlatform) return false
      return true
    },
  },

  methods: {
    getUserData() {
      try { return JSON.parse(localStorage.getItem('userData') || '{}') } catch { return {} }
    },

    next() {
      if (this.step === 1 && !this.form.niche) return
      if (this.step === 2 && !this.canProceedStep2) return
      if (this.step === 3 && !this.form.goal) return
      if (this.step === 4 && !this.form.referral) return
      if (this.step < 5) this.step++
    },
    prev() { if (this.step > 0) this.step-- },

    async finish() {
      if (!this.form.referral || this.saving) return
      this.saving = true
      const ud = this.getUserData()
      try {
        await api.put(`/usuario/${ud.id}`, {
          user_onboarded: 1,
          user_onboarding_site_url:     this.form.siteUrl || null,
          user_onboarding_niche:        this.form.niche || null,
          user_onboarding_uses_vsl:     this.form.usesVsl ? 1 : 0,
          user_onboarding_vsl_platform: this.form.vslPlatform || null,
          user_onboarding_goal:         this.form.goal || null,
          user_onboarding_referral:     this.form.referral || null,
        })
      } catch (e) {
        console.error('Erro ao salvar onboarding:', e)
        // Mesmo com erro na API, marca localmente e avança — não bloqueia o usuario
      }
      ud.user_onboarded = 1
      localStorage.setItem('userData', JSON.stringify(ud))
      this.saving = false
      this.step = 5
    },

    close() {
      this.visible = false
      this.$emit('completed')
    },
  },
}
</script>

<style scoped>
.ob-overlay {
  position: fixed; inset: 0;
  background: var(--dk-overlay, rgba(13,27,46,0.8));
  backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  z-index: 99999; padding: 16px;
}

.ob-card {
  background: var(--dk-bg-card, #fff);
  border-radius: 20px;
  width: 100%; max-width: 520px;
  box-shadow: var(--dk-shadow-xl, 0 30px 80px rgba(0,0,0,0.3));
  overflow: hidden;
  position: relative;
}

.ob-topbar {
  display: flex; align-items: center;
  padding: 14px 20px 0;
  gap: 12px;
}
.ob-progress {
  flex: 1; height: 4px; background: var(--dk-border-light, #f0f2f5);
  border-radius: 4px;
}
.ob-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #E86A10, #F5B935);
  border-radius: 4px;
  transition: width 0.4s ease;
}
.ob-step-counter {
  font-size: 0.72rem; font-weight: 700;
  color: var(--dk-text-muted, #9ca5b0);
  white-space: nowrap;
}

.ob-step {
  padding: 32px 32px 36px;
  display: flex; flex-direction: column;
  gap: 20px;
}
.ob-step--done { align-items: center; text-align: center; }

.ob-emoji { font-size: 48px; line-height: 1; }

.ob-title {
  font-size: 1.4rem; font-weight: 800;
  color: var(--dk-text-primary, #0D3349);
  margin: 0; line-height: 1.2;
}
.ob-desc {
  font-size: 0.92rem; color: var(--dk-text-secondary, #6b7a8d);
  line-height: 1.6; margin: 0;
}

.ob-step-header { display: flex; align-items: center; gap: 10px; }
.ob-step-title {
  font-size: 1.1rem; font-weight: 700;
  color: var(--dk-text-primary, #0D3349); margin: 0;
}

/* Form */
.ob-field { display: flex; flex-direction: column; gap: 8px; }
.ob-label {
  font-size: 0.83rem; font-weight: 600;
  color: var(--dk-text-secondary, #475569);
}
.ob-required-star { color: #E86A10; }
.ob-input {
  padding: 10px 14px; border-radius: 10px;
  border: 1.5px solid var(--dk-border-input, #e4e8ef);
  background: var(--dk-bg-input, #f9fafb);
  font-size: 0.88rem; color: var(--dk-text-primary, #0D3349);
  outline: none; transition: border-color 0.15s;
}
.ob-input:focus { border-color: #E86A10; }
.ob-input::placeholder { color: var(--dk-text-muted, #9ca5b0); }
.ob-hint {
  font-size: 0.72rem; color: var(--dk-text-muted, #9ca5b0);
}

/* Yes/No toggle */
.ob-yesno { display: flex; gap: 10px; }
.ob-yn-btn {
  flex: 1; display: flex; align-items: center; justify-content: center;
  gap: 7px; padding: 13px 16px; border-radius: 12px;
  border: 2px solid var(--dk-border-input, #e4e8ef);
  background: var(--dk-bg-input, #f9fafb);
  color: var(--dk-text-secondary, #6b7a8d);
  font-size: 0.88rem; font-weight: 700; cursor: pointer;
  transition: all 0.15s;
}
.ob-yn-btn:hover { border-color: #E86A10; color: #E86A10; }
.ob-yn-btn--active {
  background: rgba(232,106,16,0.08); border-color: #E86A10;
  color: #E86A10; box-shadow: 0 0 0 3px rgba(232,106,16,0.1);
}

/* Chips */
.ob-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.ob-chip {
  padding: 7px 14px; border-radius: 20px;
  border: 1.5px solid var(--dk-border-input, #e4e8ef);
  background: var(--dk-bg-input, #f9fafb);
  color: var(--dk-text-secondary, #6b7a8d);
  font-size: 0.8rem; font-weight: 600; cursor: pointer;
  transition: all 0.15s;
}
.ob-chip:hover { border-color: #E86A10; color: #E86A10; }
.ob-chip--active {
  background: rgba(232,106,16,0.1); border-color: #E86A10;
  color: #E86A10;
}

/* Option cards */
.ob-cards-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;
}
.ob-cards-grid--sm { grid-template-columns: repeat(4, 1fr); }

.ob-option-card {
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 16px 12px; border-radius: 12px;
  border: 1.5px solid var(--dk-border-input, #e4e8ef);
  background: var(--dk-bg-input, #f9fafb);
  color: var(--dk-text-secondary, #6b7a8d);
  font-size: 0.78rem; font-weight: 600; cursor: pointer;
  transition: all 0.15s; text-align: center;
}
.ob-option-card:hover { border-color: #E86A10; color: #E86A10; }
.ob-option-card--active {
  background: rgba(232,106,16,0.08); border-color: #E86A10;
  color: #E86A10; box-shadow: 0 0 0 3px rgba(232,106,16,0.1);
}
.ob-option-card--sm { padding: 12px 8px; font-size: 0.75rem; }

/* Navigation */
.ob-nav {
  display: flex; justify-content: space-between; gap: 10px;
  padding-top: 4px;
}

/* Buttons */
.ob-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 11px 22px; border-radius: 10px;
  font-size: 0.88rem; font-weight: 700; cursor: pointer;
  border: none; transition: all 0.15s;
}
.ob-btn--primary {
  background: #E86A10; color: #fff;
  box-shadow: 0 4px 12px rgba(232,106,16,0.3);
}
.ob-btn--primary:hover { background: #d05e0d; transform: translateY(-1px); }
.ob-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
.ob-btn--ghost {
  background: var(--dk-bg-inset, #f5f6fa);
  color: var(--dk-text-secondary, #6b7a8d);
  border: 1px solid var(--dk-border, #e4e8ef);
}
.ob-btn--ghost:hover { border-color: var(--dk-text-muted); }
.ob-btn--lg { padding: 14px 28px; font-size: 0.95rem; }

.ob-btn-spinner {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
  animation: ob-spin 0.6s linear infinite;
}
@keyframes ob-spin { to { transform: rotate(360deg); } }

/* Tips */
.ob-tips {
  display: flex; flex-direction: column; gap: 8px;
  width: 100%; text-align: left;
  background: var(--dk-bg-inset, #f8fafc);
  border: 1px solid var(--dk-border, #edf1f4);
  border-radius: 12px; padding: 16px 18px;
}
.ob-tip {
  display: flex; align-items: center; gap: 10px;
  font-size: 0.84rem; color: var(--dk-text-secondary, #475569);
  font-weight: 500;
}
.ob-tip svg { color: #E86A10; flex-shrink: 0; }

/* Required hint */
.ob-required-hint {
  font-size: 0.75rem;
  color: var(--dk-text-muted, #9ca5b0);
  text-align: center;
  display: block;
  margin-top: -8px;
}

/* Slide animation for VSL platform reveal */
.ob-slide-enter-active { transition: all 0.25s ease; }
.ob-slide-leave-active { transition: all 0.2s ease; }
.ob-slide-enter, .ob-slide-leave-to { opacity: 0; transform: translateY(-8px); }

/* Fade transition */
.ob-fade-enter-active, .ob-fade-leave-active { transition: opacity 0.3s; }
.ob-fade-enter, .ob-fade-leave-to { opacity: 0; }

@media (max-width: 560px) {
  .ob-step { padding: 24px 20px 28px; }
  .ob-cards-grid--sm { grid-template-columns: repeat(2, 1fr); }
}
</style>
